// components/BagLabelModal.jsx
// Add/Edit dialog for Bag Traceability labels. Clones the CaseTagModal staged-upload
// pattern; scope fields (Customers / Commodities / Types / Styles) are dropdown-add +
// removable pill lists with sentinel exclusivity: 'All' / 'Any' replaces the list, picking
// a specific value replaces the sentinel, and an emptied list snaps back to the sentinel.
// Commodity pills carry the app-wide commodity colorway; every other pill is grey.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { X, Upload, Trash2 } from 'lucide-react';
import authAxios from '../utils/authAxios';
import SecureImage from './SecureImage';
import TypeaheadSelect from './TypeaheadSelect';
import StatusPicker from './StatusPicker';
import { Input, Button, DangerButton } from '../styles/TableStyles';
import { SizeTag } from '../styles/CustomerSpecsIndexStyles';
import { BAG_LABEL_STATUSES, bagLabelStatusColor, normalizeBagLabelStatus } from '../utils/bagLabelStatus';
import { commodityColor, COMMODITY_NEUTRAL } from '../utils/commodityColor';

export const BAGLABEL_FOLDER = 'CustomerSpecs/BagLabels';

const ALL_CUSTOMERS = 'All';
const ANY_SENTINEL = 'Any';

// Reduce a stored ImageLink (bare name, foldered path, or full URL) to a bare filename.
export function toFilename(link) {
  if (!link) return '';
  let s = String(link).split('?')[0].split('#')[0];
  s = s.split('/').pop().split('\\').pop();
  try { s = decodeURIComponent(s); } catch { /* leave as-is */ }
  return s.trim();
}

// Sentinel-aware CSV → pill list ('All'/'Any' or blank collapses to the sentinel).
function listFromCsv(csv, sentinel) {
  const s = String(csv ?? '').trim();
  if (!s || s === sentinel) return [sentinel];
  return s.split(',').map((t) => t.trim()).filter(Boolean);
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: var(--shadow-a35);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Dialog = styled.div`
  background: var(--white);
  border-radius: 16px;
  box-shadow: 0 24px 64px var(--shadow-a25);
  width: min(520px, 100%);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  padding: 22px 22px 18px;
  position: relative;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 30px;
  height: 30px;
  background: transparent;
  border: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ink-a60);
  cursor: pointer;

  &:hover { background: var(--shadow-a05); color: var(--black); }
  &:focus-visible { outline: 2px solid var(--green-800); outline-offset: 2px; }
`;

const Title = styled.h2`
  margin: 0 0 14px;
  font-size: 1.05rem;
  color: var(--green-800);
  font-weight: 700;
`;

const Field = styled.div`
  margin-bottom: 14px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--ink-a70);
  margin-bottom: 5px;
`;

const ImageRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
`;

// Shrink-wraps the preview so the frame matches the uploaded image's aspect ratio
// (label artwork is rarely square, and cropping it to one hid part of the label).
const ThumbWrap = styled.div`
  position: relative;
  display: inline-block;
  line-height: 0;
  min-width: 60px;
  min-height: 40px;
`;

const thumbStyle = {
  maxWidth: '220px',
  maxHeight: '160px',
  width: 'auto',
  height: 'auto',
  objectFit: 'contain',
  borderRadius: '8px',
  border: '1px solid var(--shadow-a08)',
  display: 'block',
};

const RemoveBtn = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  border-radius: 12px;
  background: var(--red-600);
  color: var(--white);
  border: 2px solid var(--white);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 6px var(--shadow-a25);

  &:hover { background: var(--color-danger); }
  &:focus-visible { outline: 2px solid var(--green-800); outline-offset: 2px; }
`;

const NoImage = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 8px;
  border: 1px dashed var(--shadow-a15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ink-a45);
  font-size: 0.82rem;
`;

const UploadLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 14px;
  border-radius: 8px;
  background: var(--green-800);
  color: var(--white);
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;

  &:hover { opacity: 0.92; }
`;

const ErrorText = styled.p`
  margin: 0 0 12px;
  color: var(--color-danger);
  font-size: 0.85rem;
  font-weight: 600;
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
`;

// A removable chip rendered as a real <button> so it is keyboard-operable
// (mirrors the SpecFormFields RemovableTag — click removes, no ✕ glyph).
// `paint` overrides the default grey so commodity chips can carry their colorway.
const RemovableTag = ({ label, onRemove, disabled, paint }) => (
  <SizeTag
    as="button"
    type="button"
    aria-label={`Remove ${label}`}
    onClick={disabled ? undefined : onRemove}
    style={{
      cursor: disabled ? 'default' : 'pointer',
      background: paint ? paint.bg : 'var(--grey-600)',
      ...(paint ? { border: `1px solid ${paint.border}` } : null),
    }}
  >
    {label}
  </SizeTag>
);

export default function BagLabelModal({
  open,
  mode = 'add', // 'add' | 'edit'
  row,
  customers = [],
  commodities = [],
  packTypes = [],
  imgVersion = 0,
  onClose,
  onSaved,
  onRequestDelete,
  busy = false,
}) {
  const [name, setName] = useState('');
  const [customerIds, setCustomerIds] = useState([ALL_CUSTOMERS]);
  const [commodityIds, setCommodityIds] = useState([ANY_SENTINEL]);
  const [typeNames, setTypeNames] = useState([ANY_SENTINEL]);
  const [styleIds, setStyleIds] = useState([ANY_SENTINEL]);
  const [isActive, setIsActive] = useState(true);
  const [stagedFile, setStagedFile] = useState(null);
  const [stagedPreview, setStagedPreview] = useState(null);
  const [removeExisting, setRemoveExisting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const previewRef = useRef(null);

  // Initialize whenever the modal opens for a (new) row.
  useEffect(() => {
    if (!open) return;
    setName(row?.LabelName ?? '');
    setCustomerIds(listFromCsv(row?.CustomersCsv, ALL_CUSTOMERS));
    setCommodityIds(listFromCsv(row?.CommoditiesCsv, ANY_SENTINEL));
    setTypeNames(listFromCsv(row?.TypesCsv, ANY_SENTINEL));
    setStyleIds(listFromCsv(row?.StylesCsv, ANY_SENTINEL));
    setIsActive(row ? Boolean(row.IsActive) : true);
    setStagedFile(null);
    setStagedPreview(null);
    setRemoveExisting(false);
    setSaving(false);
    setError('');
  }, [open, row]);

  // Type groups feed the Types dropdown — derived from the page's packTypes rows
  // (same source as GET /api/packtypes/groups server-side), so no per-open fetch.
  const typeGroups = useMemo(() => {
    const rows = Array.isArray(packTypes) ? packTypes : [];
    return [...new Set(rows.map((r) => String(r.Type || '').trim()).filter(Boolean))].sort();
  }, [packTypes]);

  // Revoke the local preview object URL when it changes / unmounts.
  useEffect(() => {
    previewRef.current = stagedPreview;
    return () => { if (previewRef.current) URL.revokeObjectURL(previewRef.current); };
  }, [stagedPreview]);

  // Esc closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const existingFilename = useMemo(() => toFilename(row?.ImageLink), [row]);
  const hasExisting = mode === 'edit' && existingFilename && !removeExisting && !stagedFile;

  const customerNameById = useMemo(() => {
    const m = new Map();
    for (const c of customers || []) m.set(String(c.UniqueID), String(c.Customer || ''));
    return m;
  }, [customers]);

  const commodityNameById = useMemo(() => {
    const m = new Map();
    for (const c of commodities || []) m.set(String(c.UniqueID), String(c.Commodity || ''));
    return m;
  }, [commodities]);

  const styleNameById = useMemo(() => {
    const m = new Map();
    for (const r of packTypes || []) m.set(String(r.UniqueID), String(r.Style || ''));
    return m;
  }, [packTypes]);

  const customerOptions = useMemo(() => {
    const selected = new Set(customerIds);
    const opts = [];
    if (!selected.has(ALL_CUSTOMERS)) opts.push({ value: ALL_CUSTOMERS, label: 'All Customers' });
    for (const c of customers || []) {
      const v = String(c.UniqueID);
      if (!selected.has(v)) opts.push({ value: v, label: String(c.Customer || '') });
    }
    return opts;
  }, [customers, customerIds]);

  const commodityOptions = useMemo(() => {
    const selected = new Set(commodityIds);
    const opts = [];
    if (!selected.has(ANY_SENTINEL)) opts.push({ value: ANY_SENTINEL, label: 'Any' });
    for (const c of commodities || []) {
      const v = String(c.UniqueID);
      if (!selected.has(v)) opts.push({ value: v, label: String(c.Commodity || '') });
    }
    return opts;
  }, [commodities, commodityIds]);

  const typeOptions = useMemo(() => {
    const selected = new Set(typeNames);
    const opts = [];
    if (!selected.has(ANY_SENTINEL)) opts.push({ value: ANY_SENTINEL, label: 'Any' });
    for (const t of typeGroups) if (!selected.has(t)) opts.push({ value: t, label: t });
    return opts;
  }, [typeGroups, typeNames]);

  // Style pool narrows to the selected Types (Any → all pack types).
  const allowedStyleRows = useMemo(() => {
    const rows = Array.isArray(packTypes) ? packTypes : [];
    if (typeNames.includes(ANY_SENTINEL)) return rows;
    const allowed = new Set(typeNames.map((t) => String(t).trim().toUpperCase()));
    return rows.filter((r) => allowed.has(String(r.Type || '').trim().toUpperCase()));
  }, [packTypes, typeNames]);

  const styleOptions = useMemo(() => {
    const selected = new Set(styleIds);
    const opts = [];
    if (!selected.has(ANY_SENTINEL)) opts.push({ value: ANY_SENTINEL, label: 'Any' });
    for (const r of allowedStyleRows) {
      const v = String(r.UniqueID);
      if (!selected.has(v)) opts.push({ value: v, label: String(r.Style || '') });
    }
    return opts;
  }, [allowedStyleRows, styleIds]);

  if (!open) return null;

  const addToList = (list, value, sentinel) => {
    if (value === sentinel) return [sentinel];
    return Array.from(new Set([...list.filter((x) => x !== sentinel), value]));
  };
  const removeFromList = (list, value, sentinel) => {
    const rest = list.filter((x) => x !== value);
    return rest.length ? rest : [sentinel];
  };

  // Drop style pins whose Type falls outside the (new) Types scope.
  const pruneStyles = (nextTypes) => {
    setStyleIds((prev) => {
      if (prev.includes(ANY_SENTINEL) || nextTypes.includes(ANY_SENTINEL)) return prev;
      const allowed = new Set(nextTypes.map((t) => String(t).trim().toUpperCase()));
      const typeById = new Map((packTypes || []).map((r) => [String(r.UniqueID), String(r.Type || '').trim().toUpperCase()]));
      const kept = prev.filter((id) => allowed.has(typeById.get(id)));
      return kept.length ? kept : [ANY_SENTINEL];
    });
  };

  const addCustomer = (v) => {
    const val = String(v || '').trim();
    if (val) setCustomerIds((prev) => addToList(prev, val, ALL_CUSTOMERS));
  };
  const addCommodity = (v) => {
    const val = String(v || '').trim();
    if (val) setCommodityIds((prev) => addToList(prev, val, ANY_SENTINEL));
  };
  // Sentinel chips — and ids the dropdown can't name — stay neutral grey; a resolved
  // commodity carries its colorway.
  const commodityPaint = (id) => {
    const name = id === ANY_SENTINEL ? '' : (commodityNameById.get(id) || '');
    return name ? commodityColor(name) : COMMODITY_NEUTRAL;
  };
  const addType = (v) => {
    const val = String(v || '').trim();
    if (!val) return;
    const next = addToList(typeNames, val, ANY_SENTINEL);
    setTypeNames(next);
    pruneStyles(next);
  };
  const removeType = (val) => {
    const next = removeFromList(typeNames, val, ANY_SENTINEL);
    setTypeNames(next);
    pruneStyles(next);
  };
  const addStyle = (v) => {
    const val = String(v || '').trim();
    if (val) setStyleIds((prev) => addToList(prev, val, ANY_SENTINEL));
  };

  const pickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (stagedPreview) URL.revokeObjectURL(stagedPreview);
    setStagedFile(file);
    setStagedPreview(URL.createObjectURL(file));
    setRemoveExisting(false);
  };

  const clearStaged = () => {
    if (stagedPreview) URL.revokeObjectURL(stagedPreview);
    setStagedFile(null);
    setStagedPreview(null);
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    const trimmed = name.trim();
    if (!trimmed) { setError('Label Name is required'); return; }
    setError('');
    setSaving(true);
    try {
      const csvFrom = (list, sentinel) => (list.includes(sentinel) || list.length === 0 ? sentinel : list.join(','));
      const fd = new FormData();
      fd.append('LabelName', trimmed);
      fd.append('CustomersCsv', csvFrom(customerIds, ALL_CUSTOMERS));
      fd.append('CommoditiesCsv', csvFrom(commodityIds, ANY_SENTINEL));
      fd.append('TypesCsv', csvFrom(typeNames, ANY_SENTINEL));
      fd.append('StylesCsv', csvFrom(styleIds, ANY_SENTINEL));
      fd.append('IsActive', String(isActive));
      if (stagedFile) fd.append('image', stagedFile);
      else if (removeExisting) fd.append('removeImage', 'true');

      const res = mode === 'edit'
        ? await authAxios.put(`/api/baglabels/${row.UniqueID}`, fd)
        : await authAxios.post('/api/baglabels', fd);

      // Reset before notifying so the saved/false invariant holds regardless of
      // whether the parent unmounts the modal.
      setSaving(false);
      onSaved && onSaved({
        oldImageLink: row?.ImageLink ?? null,
        newImageLink: res?.data?.ImageLink ?? null,
      });
    } catch (err) {
      console.error('Save Bag Label failed', err);
      const status = err?.response?.status;
      if (status === 409) setError(err?.response?.data?.error || 'A bag label with this name already exists.');
      else if (status === 413) setError('Image too large (max 10 MB).');
      else if (status === 400) setError(err?.response?.data?.error || 'Invalid input.');
      else setError('Could not save bag label.');
      setSaving(false);
    }
  };

  const disabled = saving || busy;

  return (
    <Backdrop onClick={onClose} role="presentation">
      <Dialog onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="baglabel-modal-title">
        <CloseBtn type="button" onClick={onClose} aria-label="Close dialog">
          <X size={16} aria-hidden="true" />
        </CloseBtn>

        <Title id="baglabel-modal-title">{mode === 'edit' ? 'Edit Bag Label' : 'Add Bag Label'}</Title>

        <form onSubmit={handleSave}>
          <Field>
            <Label htmlFor="baglabel-name">Label Name</Label>
            <Input
              id="baglabel-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Walmart 2lb Bag"
              autoFocus
            />
          </Field>

          <Field>
            <Label>Customers</Label>
            <TypeaheadSelect
              ariaLabel="Add customer"
              value=""
              placeholder="Add customer..."
              size="md"
              options={customerOptions}
              onChange={addCustomer}
            />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
              {customerIds.map((id) => (
                <RemovableTag
                  key={id}
                  label={id === ALL_CUSTOMERS ? 'All Customers' : (customerNameById.get(id) || `#${id}`)}
                  onRemove={() => setCustomerIds((prev) => removeFromList(prev, id, ALL_CUSTOMERS))}
                />
              ))}
            </div>
          </Field>

          <Field>
            <Label>Commodities</Label>
            <TypeaheadSelect
              ariaLabel="Add commodity"
              value=""
              placeholder="Add commodity..."
              size="md"
              options={commodityOptions}
              onChange={addCommodity}
            />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
              {commodityIds.map((id) => (
                <RemovableTag
                  key={id}
                  label={id === ANY_SENTINEL ? 'Any' : (commodityNameById.get(id) || `#${id}`)}
                  paint={commodityPaint(id)}
                  onRemove={() => setCommodityIds((prev) => removeFromList(prev, id, ANY_SENTINEL))}
                />
              ))}
            </div>
          </Field>

          <Field>
            <Label>Types</Label>
            <TypeaheadSelect
              ariaLabel="Add type"
              value=""
              placeholder="Add type..."
              size="md"
              options={typeOptions}
              onChange={addType}
            />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
              {typeNames.map((t) => (
                <RemovableTag key={t} label={t} onRemove={() => removeType(t)} />
              ))}
            </div>
          </Field>

          <Field>
            <Label>Styles</Label>
            <TypeaheadSelect
              ariaLabel="Add style"
              value=""
              placeholder="Add style..."
              size="md"
              options={styleOptions}
              onChange={addStyle}
            />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
              {styleIds.map((id) => (
                <RemovableTag
                  key={id}
                  label={id === ANY_SENTINEL ? 'Any' : (styleNameById.get(id) || `#${id}`)}
                  onRemove={() => setStyleIds((prev) => removeFromList(prev, id, ANY_SENTINEL))}
                />
              ))}
            </div>
          </Field>

          <Field>
            <Label>Status</Label>
            <StatusPicker
              status={isActive ? 'Active' : 'Inactive'}
              statuses={BAG_LABEL_STATUSES}
              colorFor={bagLabelStatusColor}
              normalize={normalizeBagLabelStatus}
              ariaLabel="Bag label status"
              onChange={(next) => setIsActive(next === 'Active')}
            />
          </Field>

          <Field>
            <Label>Image</Label>
            <ImageRow>
              {stagedFile ? (
                <ThumbWrap>
                  <img src={stagedPreview} alt="New preview" style={thumbStyle} />
                  <RemoveBtn type="button" onClick={clearStaged} aria-label="Remove selected image">
                    <X size={14} aria-hidden="true" />
                  </RemoveBtn>
                </ThumbWrap>
              ) : hasExisting ? (
                <ThumbWrap>
                  <SecureImage
                    key={`modal-${row?.UniqueID}-${imgVersion}`}
                    src={existingFilename}
                    folder={BAGLABEL_FOLDER}
                    alt={name}
                    style={thumbStyle}
                  />
                  <RemoveBtn type="button" onClick={() => setRemoveExisting(true)} aria-label="Remove current image">
                    <X size={14} aria-hidden="true" />
                  </RemoveBtn>
                </ThumbWrap>
              ) : (
                <NoImage>No image</NoImage>
              )}

              <UploadLabel>
                <Upload size={16} aria-hidden="true" />
                {stagedFile || hasExisting ? 'Change Image' : 'Upload Image'}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={pickFile}
                  style={{ display: 'none' }}
                />
              </UploadLabel>
            </ImageRow>
          </Field>

          {error && <ErrorText>{error}</ErrorText>}

          <Foot>
            {mode === 'edit' && (
              <DangerButton type="button" onClick={() => onRequestDelete && onRequestDelete(row)} disabled={disabled}>
                <Trash2 size={15} aria-hidden="true" /> Delete
              </DangerButton>
            )}
            <div style={{ flex: 1 }} />
            <Button type="button" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={disabled}>{saving ? 'Saving…' : 'Save'}</Button>
          </Foot>
        </form>
      </Dialog>
    </Backdrop>
  );
}
